import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Grid, Typography, TextField, InputAdornment, withStyles } from '@material-ui/core';
import DefaultOption from '@configuratorware/configurator-frontendgui/App/Modules/Creator/Components/Option';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { removeAllDoors } from '../../Reducers/Configurator/Actions';
import clsx from 'clsx';
import Markdown from '@configuratorware/configurator-frontendgui/Framework/Components/Markdown';
import customClassName from '@configuratorware/configurator-frontendgui/App/Utils/customClassName';
import InfoRoundedIcon from '@material-ui/icons/InfoRounded';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import BlockRoundedIcon from '@material-ui/icons/BlockRounded';

const StyledDefaultOption = withStyles(() => ({
  root: {
    padding: 0,
    width: 'auto',
    minHeight: 'auto',
    marginBottom: 32,
    display: 'flex',
    '& .content': {
      flex: 1,
      '& .MuiGrid-item': {
        alignContent: 'center',
      },
    },
  },
}))(DefaultOption);

const getAttributeValue = (option, attributeIdentifier, defaultValue) =>
  option.attributes?.find(({ identifier }) => attributeIdentifier === identifier)?.values?.[0]?.value ||
  defaultValue;

const OptionWithInput = (props) => {
  const { option, optionclassification, onInputTextChange } = props;
  const { selectedoptions } = optionclassification;
  const inputRef = useRef();
  const [inputText, setInputText] = useState(
    selectedoptions?.find(({ identifier }) => identifier === option.identifier)?.inputText || ''
  );
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const handleTextInputChange = (event) => {
    let value = event.target.value;
    if (value.match(/[^0-9]/)) {
      event.preventDefault();
      return;
    }
    setInputText(value);
  };

  const handleTextInputBlur = (event) => {
    let value = event.target.value;
    const min = getAttributeValue(option, 'value_min', 1);
    const max = getAttributeValue(option, 'value_max');
    if (value < min) {
      value = min;
      setShowErrorMessage(true);
    } else if (value > max) {
      value = max;
      setShowErrorMessage(true);
    } else if (showErrorMessage) {
      setShowErrorMessage(false);
    }
    setInputText(value);
    onInputTextChange(option.identifier, `${value}`);
  };
  return (
    <StyledDefaultOption {...props} selected={false} onSelect={() => inputRef.current.focus()}>
      {({ classes, option }) => {
        return (
          <Grid container spacing={1}>
            <Grid item xs={8}>
              <Typography component={'div'} className={classes.optionList} variant={'body2'}>
                <div className={classes.title}>{option.title}</div>
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <TextField
                variant="outlined"
                size="small"
                label={''}
                value={inputText}
                onChange={handleTextInputChange}
                onBlur={handleTextInputBlur}
                inputRef={inputRef}
                InputProps={{
                  endAdornment: <InputAdornment position="end">mm</InputAdornment>,
                }}
              />
            </Grid>
            {showErrorMessage && (
              <Typography variant={'body4'} style={{ color: 'red', marginLeft: '5px' }}>
                {t(`measurements.${option.identifier}.errorMessage`)}
              </Typography>
            )}
          </Grid>
        );
      }}
    </StyledDefaultOption>
  );
};

const OptionWithCustomIncompatibility = (props) => {
  const translations = {
    ...t('common'),
    ...t('optionlistitem'),
  };
  const renderActions = (props) => {
    const { shouldDisplayDetails, onDetailsClicked, classes, onIncompatibilityClicked, option } = props;
    const displayDetails = shouldDisplayDetails();
    const resultStatus =
      option.check_results && 'status' in option.check_results ? option.check_results.status : true;
    const showDefaultIncompatibility = resultStatus !== true;
    const incompatibleHeight = option.incompatibleHeight || false;

    return (
      <div className={classes.details}>
        {displayDetails && (
          <div>
            <a className={classes.iconWithText} onClick={onDetailsClicked}>
              <InfoRoundedIcon
                className={clsx(classes.materialIcon, customClassName('rule-info-icon-option'))}
              />
              <Typography variant={'body2'} className={classes.iconWithText}>
                {translations.details}
              </Typography>
            </a>
          </div>
        )}

        {showDefaultIncompatibility && (
          <div>
            <a className={classes.iconWithText} onClick={onIncompatibilityClicked}>
              <WarningRoundedIcon
                className={clsx(classes.materialIcon, customClassName('rule-alert-icon-option'))}
              />
              <Typography variant={'body2'}>{translations.incompatibility}</Typography>
            </a>
          </div>
        )}

        {incompatibleHeight && (
          <div>
            <div className={classes.iconWithText}>
              <BlockRoundedIcon className={classes.materialIcon} />{' '}
              <Typography variant={'body2'}>{t('option.incompatibleHeight')}</Typography>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderText = (props) => {
    const { option, classes, onAbstractClicked, hidePrices } = props;
    return (
      <Grid item xs={12}>
        <Typography component={'div'} className={classes.optionList} variant={'body2'}>
          <div className={classes.title}>{option.title}</div>
          {!hidePrices && <div className={classes.price}>{option.priceFormatted}</div>}
          {option.abstract && (
            <Markdown className={classes.description} onClick={onAbstractClicked}>
              {option.abstract}
            </Markdown>
          )}
          {renderActions(props)}
        </Typography>
      </Grid>
    );
  };

  return (
    <DefaultOption
      {...props}
      render={(props) => {
        const {
          getRootClassName,
          onOptionClicked,
          getContentClassName,
          classes,
          imageAvailable,
          renderImage,
          renderNotAvailableImage,
          smallerScreen,
          renderControls,
          option,
        } = props;

        const disabled = option.incompatibleHeight || false;

        return (
          <div
            className={clsx(getRootClassName(), disabled && 'disabled')}
            data-option={option.identifier}
            onClick={onOptionClicked}
          >
            <div className={clsx(getContentClassName(), disabled && 'disabled')}>
              <>
                <div className={classes.image}>
                  {imageAvailable ? renderImage() : renderNotAvailableImage()}
                </div>

                <div className={classes.text}>{renderText(props)}</div>

                {!smallerScreen && renderControls()}
              </>
            </div>
          </div>
        );
      }}
    />
  );
};

const Option = (props) => {
  const { option, onSelect, optionclassification } = props;
  const dispatch = useDispatch();

  const getPriceValue = (option) => {
    let priceValue = option.priceFormatted;
    const calculationMethodAttributeValue = getAttributeValue(option, 'calculation_method');
    if (calculationMethodAttributeValue === 'meters') {
      priceValue = option.priceFormatted + ' ' + t('option.perMeter');
    } else if (calculationMethodAttributeValue === 'squaremeters') {
      priceValue = option.priceFormatted + ' ' + t('option.perSquareMeter');
    }
    return priceValue;
  };

  const onOptionSelection = async (selectedOption, amount) => {
    await onSelect(selectedOption, amount);
    if (optionclassification.identifier === 'door_types' && selectedOption.identifier === 'no_door') {
      dispatch(removeAllDoors());
    }
  };

  const OptionComponent = option.hasTextinput ? OptionWithInput : OptionWithCustomIncompatibility;

  return (
    <OptionComponent
      key={option.identifier}
      {...props}
      onSelect={onOptionSelection}
      option={{ ...option, priceFormatted: getPriceValue(option) }}
    />
  );
};

Option.propTypes = {
  optionclassification: PropTypes.object.isRequired,
  option: PropTypes.object.isRequired,
};

export default Option;
